import { computePosition } from '@floating-ui/dom'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { render, unmountComponentAtNode } from 'react-dom'

import { tablePluginKey as TableRightClickMenuPluginKey } from 'common/Prose/Plugins/TableRightClickMenu/TableRightClickMenu'
import { isTableSelection } from 'common/Prose/utils/table'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { TableFormatBar } from '../components/TableFormatBar/TableFormatBar'
import Styles from './TableFormatBarPlugin.module.scss'
import { QueryClientProvider } from 'react-query'
import queryClient from 'queryClient'
import { Provider } from 'react-redux'
import reduxStore from 'redux/store'

interface PluginState {
  decoration: Decoration | null
  wrapper: HTMLElement | null
}

export const TableFormatBarPluginKey = new PluginKey('TableFormatBar')
export const TableFormatBarPlugin = new Plugin({
  key: TableFormatBarPluginKey,
  props: {
    decorations: (editorState) => {
      const { decoration }: PluginState = TableFormatBarPluginKey.getState(editorState)
      if (!decoration) return

      return DecorationSet.create(editorState.doc, [decoration])
    },
  },
  state: {
    apply: (tr, pluginState, oldEditorState, newEditorState): PluginState => {
      // NOTE: if the format bar should be hidden
      if (
        TableRightClickMenuPluginKey.getState(newEditorState)?.active ||
        !isTableSelection(newEditorState.selection)
      ) {
        const { wrapper } = pluginState
        if (wrapper) unmountComponentAtNode(wrapper)

        return { decoration: null, wrapper: null }
      }

      // NOTE: if the doc didn't change and the format bar didn't become active
      // either
      if (oldEditorState.selection.eq(newEditorState.selection) && !tr.docChanged) return pluginState

      // NOTE: in all other cases, construct a new decoration so react has a
      // chance to re-render (or initially render)
      const { wrapper: existingWrapper }: PluginState = TableFormatBarPluginKey.getState(oldEditorState)
      const wrapper = existingWrapper ?? document.createElement('div')
      if (!existingWrapper) {
        wrapper.classList.add(Styles.wrapper)
        // NOTE: Not sure why this is necessary, but without it the bar
        // disappears, because prosemirror detects dom mutations?
        wrapper.addEventListener('mousedown', (e) => e.preventDefault())
      }

      return {
        decoration: Decoration.widget(
          newEditorState.selection.from,
          (view) => {
            // NOTE: wait for the table to update its dimensions
            setTimeout(() => {
              ;(async () => {
                const tableElement = view.domAtPos(view.state.selection.from + 1)?.node?.parentElement as
                  | HTMLElement
                  | undefined
                if (!tableElement) return

                const tableWrapper = tableElement.parentElement?.parentElement
                if (!tableWrapper) return

                // NOTE: we get top/right from different elements, because
                // the tablewrapper receives a padding based on scroll
                // status of the table. The table doesn't, but we don't
                // want to scroll the format bar with it, so we use the
                // left position from the wrapper
                const { y } = await computePosition(tableElement, wrapper, {
                  placement: 'top',
                })

                const { x } = await computePosition(tableWrapper, wrapper, {
                  placement: 'top',
                })

                Object.assign(wrapper.style, {
                  left: `${x}px`,
                  top: `${y}px`,
                  opacity: '1',
                })
              })()
            })
            const editorStateStore = new AlmanacEditorState(view)

            render(
              <AlmanacEditorStateContext.Provider value={editorStateStore}>
                <Provider store={reduxStore}>
                  <QueryClientProvider client={queryClient}>
                    <TableFormatBar view={view} />
                  </QueryClientProvider>
                </Provider>
              </AlmanacEditorStateContext.Provider>,
              wrapper,
            )

            return wrapper
          },
          { stopEvent: () => true },
        ),
        wrapper,
      }
    },
    init: (): PluginState => ({ decoration: null, wrapper: null }),
  },
})
